import { useCallback, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import {
  OrganizationMemberRoles,
  type OrganizationMemberResponse,
  type OrganizationMemberRolesType,
} from '@/api/types/Organization'
import { statesData, type StateData } from '@/utils/stateData'

interface OrganizationMemberFormType {
  avatar?: File | null
  previousAvatar?: string
  firstName: OrganizationMemberResponse['details']['firstName']
  lastName?: OrganizationMemberResponse['details']['lastName']
  email: OrganizationMemberResponse['email']
  city?: OrganizationMemberResponse['details']['city']
  state?: StateData['state']
  role: OrganizationMemberRolesType
}

const useOrganizationMemberForm = (member: Partial<OrganizationMemberResponse> | null) => {
  const defaultValues = useCallback((m: Partial<OrganizationMemberResponse> | null): OrganizationMemberFormType => ({
    avatar: undefined,
    previousAvatar: m?.assets?.PROFILE_PICTURE || '',
    firstName: m?.details?.firstName || '',
    lastName: m?.details?.lastName || '',
    email: m?.email || '',
    city: m?.details?.city || '',
    state: m?.details?.state || '',
    role: m?.role || OrganizationMemberRoles[2], // Default to 'DELEGATE'
  }), [])

  const validationSchema = useMemo(() => z.object({
    avatar: z.instanceof(File).nullish(),
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().optional(),
    email: z.email('Invalid email address'),
    city: z.string().optional(),
    state: z.string().refine(
      val => !val || statesData.some(s => s.state === val),
      { message: 'Invalid state' }
    ).optional(),
    role: z.enum(OrganizationMemberRoles, { message: 'Role is required' }),
  }), [])

  const formDefaults = useMemo(() => defaultValues(member), [member, defaultValues])

  const form = useForm<OrganizationMemberFormType>({
    defaultValues: formDefaults,
    resolver: zodResolver(validationSchema),
  })

  const resetForm = useCallback((m: Partial<any> | null) => {
    form.reset(defaultValues(m))
  }, [form, defaultValues])

  return { form, defaultValues, resetForm }
}

export default useOrganizationMemberForm
